import type { FetchLinkedUsersResponseDto } from "@/generated/model/fetchLinkedUsersResponseDto";
import type { ListManageableNurseriesResponseDto } from "@/generated/model/listManageableNurseriesResponseDto";
import type { SetupDeviceResponseDto } from "@/generated/model/setupDeviceResponseDto";
import type { SignInUserResponseDto } from "@/generated/model/signInUserResponseDto";
import { axiosClient } from "@/lib/api";

/**
 * Device setup/reset talk to the API with a TRANSIENT manager token: we call
 * `/v1/auth/login` to get an idToken and pass it as a one-shot Bearer header,
 * never signing the manager into the Firebase client. That keeps the device on
 * the setup / PIN screen (the `_auth` layout would otherwise redirect an
 * authenticated session to the dashboard) and matches NAU-226's AC that setup
 * is always email/password. The axios interceptor only injects a token when a
 * Firebase user exists, so our explicit header is preserved here.
 *
 * Fetching the roster is the exception: it carries the device's own secret, so
 * it works with no manager and no signed-in user.
 */

function authHeaders(idToken: string) {
  return { Authorization: `Bearer ${idToken}` };
}

/** Credential for the pre-auth calls, issued when the device was linked. */
export function deviceHeaders(deviceSecret: string) {
  return { "x-device-secret": deviceSecret };
}

/** Authenticate a manager for setup/reset. Throws on bad credentials (401). */
export async function loginForDeviceAuth(
  email: string,
  password: string,
): Promise<string> {
  const res = await axiosClient<SignInUserResponseDto>({
    url: "/v1/auth/login",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: { email, password },
  });
  return res.data.idToken;
}

export async function fetchManageableNurseries(
  idToken: string,
): Promise<ListManageableNurseriesResponseDto> {
  const res = await axiosClient<ListManageableNurseriesResponseDto>({
    url: "/v1/devices/manageable-nurseries",
    method: "GET",
    headers: authHeaders(idToken),
  });
  return res.data;
}

export async function setupDevice(
  idToken: string,
  deviceId: string,
  nurseryId: string,
): Promise<SetupDeviceResponseDto> {
  const res = await axiosClient<SetupDeviceResponseDto>({
    url: "/v1/devices/setup",
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(idToken) },
    data: { deviceId, nurseryId },
  });
  return res.data;
}

/**
 * Fetch the roster the PIN screen renders. Authenticated by the device's own
 * secret rather than a user token, so the screen can refresh itself while
 * nobody is signed in — that is what stops a newly added colleague being
 * invisible until someone else logs in.
 */
export async function fetchLinkedUsers(
  deviceId: string,
  deviceSecret: string,
): Promise<FetchLinkedUsersResponseDto> {
  const res = await axiosClient<FetchLinkedUsersResponseDto>({
    url: `/v1/devices/${deviceId}/linked-users`,
    method: "GET",
    headers: deviceHeaders(deviceSecret),
  });
  return res.data;
}

export async function resetDevice(
  idToken: string,
  deviceId: string,
): Promise<void> {
  await axiosClient({
    url: `/v1/devices/${deviceId}/reset`,
    method: "POST",
    headers: authHeaders(idToken),
  });
}
